export const GALLERY_CATEGORIES = [
  "Church Building", "Sunday Worship", "Holy Communion", "Christmas", "Easter",
  "Palm Sunday", "Baptism", "Sunday School", "Youth Fellowship",
  "Women's Fellowship", "Men's Fellowship", "Church Events", "Other",
];

export const ADMIN_CONFIGS = {
  events: {
    title: "Events",
    subtitle: "Create and manage church events. Past events are automatically archived on the public site.",
    apiPath: "/admin/events",
    testId: "events-admin",
    addLabel: "Add Event",
    searchPlaceholder: "Search events by title, location or category…",
    columns: [
      { key: "title", label: "Title" },
      { key: "date", label: "Date", type: "date" },
      { key: "location", label: "Location" },
      { key: "published", label: "Status", type: "status" },
    ],
    fields: [
      { key: "title", label: "Event Title", type: "text", required: true },
      { key: "date", label: "Date", type: "date", required: true },
      { key: "start_time", label: "Start Time", type: "time" },
      { key: "end_time", label: "End Time", type: "time" },
      { key: "location", label: "Location", type: "text" },
      { key: "category", label: "Category", type: "text", placeholder: "e.g. Worship, Fellowship, Outreach" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "image_url", label: "Event Photograph", type: "image" },
      { key: "published", label: "Published (visible on public website)", type: "checkbox", default: true },
    ],
  },
  announcements: {
    title: "Announcements",
    subtitle: "Publish news and updates for the congregation.",
    apiPath: "/admin/announcements",
    testId: "announcements-admin",
    addLabel: "Add Announcement",
    searchPlaceholder: "Search announcements…",
    columns: [
      { key: "title", label: "Title" },
      { key: "date", label: "Date", type: "date" },
      { key: "category", label: "Category" },
      { key: "featured", label: "Featured", type: "bool" },
      { key: "published", label: "Status", type: "status" },
    ],
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "body", label: "Description", type: "textarea", required: true },
      { key: "date", label: "Date", type: "date", required: true },
      { key: "category", label: "Category", type: "text", placeholder: "e.g. General, Service, Urgent" },
      { key: "image_url", label: "Image (optional)", type: "image" },
      { key: "featured", label: "Featured announcement", type: "checkbox" },
      { key: "published", label: "Published (visible on public website)", type: "checkbox", default: true },
    ],
  },
  gallery: {
    title: "Photo Gallery",
    subtitle: "Upload and organise church photographs. Only published photos appear publicly.",
    apiPath: "/admin/gallery",
    testId: "gallery-admin",
    addLabel: "Add Photo",
    searchPlaceholder: "Search photos…",
    columns: [
      { key: "image_url", label: "Photo", type: "image" },
      { key: "title", label: "Title" },
      { key: "category", label: "Category" },
      { key: "featured", label: "Featured", type: "bool" },
      { key: "published", label: "Status", type: "status" },
    ],
    fields: [
      { key: "image_url", label: "Photograph", type: "image", required: true },
      { key: "title", label: "Title", type: "text", required: true },
      { key: "caption", label: "Caption", type: "textarea" },
      { key: "category", label: "Category", type: "select", options: GALLERY_CATEGORIES },
      { key: "event_date", label: "Event Date", type: "date" },
      { key: "featured", label: "Featured (shown on homepage)", type: "checkbox" },
      { key: "published", label: "Published (visible on public website)", type: "checkbox", default: true },
    ],
  },
  history: {
    title: "Church History",
    subtitle: "Build the historical timeline of the church. Only published entries appear publicly.",
    apiPath: "/admin/history",
    testId: "history-admin",
    addLabel: "Add Entry",
    searchPlaceholder: "Search history entries…",
    columns: [
      { key: "year", label: "Year" },
      { key: "title", label: "Title" },
      { key: "order", label: "Order" },
      { key: "published", label: "Status", type: "status" },
    ],
    fields: [
      { key: "year", label: "Year / Date", type: "text", required: true, placeholder: "e.g. 1985" },
      { key: "title", label: "Title", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "image_url", label: "Historical Photograph", type: "image" },
      { key: "order", label: "Display Order", type: "number", default: 0 },
      { key: "published", label: "Published (visible on public website)", type: "checkbox", default: true },
    ],
  },
  leadership: {
    title: "Leadership",
    subtitle: "Manage church leadership profiles shown on the public website.",
    apiPath: "/admin/leadership",
    testId: "leadership-admin",
    addLabel: "Add Leader",
    searchPlaceholder: "Search leaders…",
    columns: [
      { key: "name", label: "Name" },
      { key: "position", label: "Position" },
      { key: "order", label: "Order" },
      { key: "published", label: "Status", type: "status" },
    ],
    fields: [
      { key: "name", label: "Full Name", type: "text", required: true },
      { key: "position", label: "Position", type: "select", options: ["Pastor", "Presbyter", "Church Secretary", "Treasurer", "Other"] },
      { key: "role", label: "Ministry / Role", type: "text" },
      { key: "bio", label: "Short Biography", type: "textarea" },
      { key: "photo_url", label: "Photograph", type: "image" },
      { key: "contact", label: "Contact (optional, shown publicly)", type: "text" },
      { key: "order", label: "Display Order", type: "number", default: 0 },
      { key: "published", label: "Visible on public website", type: "checkbox", default: true },
    ],
  },
  ministries: {
    title: "Ministries",
    subtitle: "Manage the ministries of the church.",
    apiPath: "/admin/ministries",
    testId: "ministries-admin",
    addLabel: "Add Ministry",
    searchPlaceholder: "Search ministries…",
    columns: [
      { key: "name", label: "Name" },
      { key: "leader", label: "Leader" },
      { key: "meeting_time", label: "Meeting" },
      { key: "published", label: "Status", type: "status" },
    ],
    fields: [
      { key: "name", label: "Ministry Name", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "leader", label: "Leader", type: "text" },
      { key: "meeting_time", label: "Meeting Day / Time", type: "text" },
      { key: "contact", label: "Contact (optional)", type: "text" },
      { key: "photo_url", label: "Photograph", type: "image" },
      { key: "order", label: "Display Order", type: "number", default: 0 },
      { key: "published", label: "Visible on public website", type: "checkbox", default: true },
    ],
  },
  services: {
    title: "Worship & Services",
    subtitle: "Manage the weekly worship and service schedule.",
    apiPath: "/admin/services",
    testId: "services-admin",
    addLabel: "Add Service",
    searchPlaceholder: "Search services…",
    columns: [
      { key: "name", label: "Service" },
      { key: "day", label: "Day" },
      { key: "time", label: "Time" },
      { key: "published", label: "Status", type: "status" },
    ],
    fields: [
      { key: "name", label: "Service Name", type: "text", required: true },
      { key: "day", label: "Day", type: "text", placeholder: "e.g. Sunday" },
      { key: "time", label: "Time", type: "text", placeholder: "e.g. 9:00 AM" },
      { key: "location", label: "Location", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "notes", label: "Special Instructions", type: "textarea" },
      { key: "order", label: "Display Order", type: "number", default: 0 },
      { key: "published", label: "Visible on public website", type: "checkbox", default: true },
    ],
  },
};
